import { RolodexFilePath } from 'types'

import { cantileverDocumentPageIdForPage } from './cantileverHostIds'

type ImageVariant = 'thumbnail' | 'hiRes'

const IMAGE_PATH_BY_VARIANT: Record<ImageVariant, string> = {
  thumbnail: 'thumbnail.png',
  hiRes: '6400px.png',
}

/**
 * Legacy page-image path: files on system-document-page entry
 * `${systemDocumentEntryId}_pg_${pageNumber}`.
 */
export function getDocumentImageQuery(
  documentId: string,
  pageNumber: number,
  variant: ImageVariant
): RolodexFilePath {
  return {
    entry_id: `${documentId}_pg_${pageNumber}`,
    path: IMAGE_PATH_BY_VARIANT[variant],
  }
}

/**
 * PDF file ref on the cantilever-document host.
 */
export function resolvePdfFileRef(
  cantileverDocumentId: string,
  filename: string
): RolodexFilePath {
  return {
    entry_id: cantileverDocumentId,
    path: filename,
  }
}

/**
 * Page image file ref on the cantilever-document-page host.
 */
export function resolvePageImageFileRef(
  cantileverDocumentPageId: string,
  variant: ImageVariant
): RolodexFilePath {
  return {
    entry_id: cantileverDocumentPageId,
    path: IMAGE_PATH_BY_VARIANT[variant],
  }
}

/**
 * Dual-read page images: prefer cantilever-document-page host when present,
 * else legacy `${systemDocumentEntryId}_pg_N` system page entry.
 */
export function resolvePageImageFileRefDual(
  systemDocumentEntryId: string | undefined,
  pageNumber: number,
  variant: ImageVariant,
  cantileverDocumentPageId?: string
): RolodexFilePath | undefined {
  if (cantileverDocumentPageId) {
    return resolvePageImageFileRef(cantileverDocumentPageId, variant)
  }
  if (systemDocumentEntryId) {
    return getDocumentImageQuery(systemDocumentEntryId, pageNumber, variant)
  }
  return undefined
}

/**
 * Primary page-image ref for list/preview: UUID5-derived cantilever page host
 * when site + bare BQ document_id are known; otherwise legacy `${id}_pg_N`.
 */
export function resolveDerivedOrLegacyPageImageFileRef(args: {
  siteId: string | undefined
  bqDocumentId: string | undefined
  systemDocumentEntryId: string | undefined
  pageNumber: number
  variant: ImageVariant
}): RolodexFilePath | undefined {
  const { siteId, bqDocumentId, systemDocumentEntryId, pageNumber, variant } =
    args
  if (siteId && bqDocumentId) {
    return resolvePageImageFileRef(
      cantileverDocumentPageIdForPage(siteId, bqDocumentId, pageNumber),
      variant
    )
  }
  if (systemDocumentEntryId) {
    return getDocumentImageQuery(systemDocumentEntryId, pageNumber, variant)
  }
  return undefined
}

/** @deprecated Use resolveDerivedOrLegacyPageImageFileRef */
export function resolveListThumbnailFileRef(args: {
  siteId: string | undefined
  bqDocumentId: string | undefined
  systemDocumentEntryId: string | undefined
  pageNumber: number
}): RolodexFilePath | undefined {
  return resolveDerivedOrLegacyPageImageFileRef({
    ...args,
    variant: 'thumbnail',
  })
}

/**
 * Dual-read PDF: prefer cantilever-document host when present, else system-document.
 */
export function resolvePdfFileRefDual(
  systemDocumentEntryId: string | undefined,
  filename: string | undefined,
  cantileverDocumentId?: string
): RolodexFilePath | undefined {
  if (!filename) return undefined
  if (cantileverDocumentId) {
    return resolvePdfFileRef(cantileverDocumentId, filename)
  }
  if (systemDocumentEntryId) {
    return { entry_id: systemDocumentEntryId, path: filename }
  }
  return undefined
}
